import React, { useState, useRef } from 'react';
import { ProcessedRow, CustomField, BatchDebugInfo } from '../types';
import { formatCompleteResponse, copyToClipboard } from '../utils/formatCompleteResponse';
import { refreshSources, getURLMappings } from '../services/api';
import './ResultsTable.css';

// Result of regeneration to update the row
export interface RegeneratedResponse {
  response: string;
  confidence: number;
  sources: { doc_name: string; url?: string }[];
}

interface ResultsTableProps {
  rows: ProcessedRow[];
  customFields: CustomField[];
  projectId: string;  // Required for refresh sources API
  onUpdateRow: (id: string, field: string, value: string) => void;
  onUpdateRowSources: (id: string, sources: { doc_name: string; url?: string }[]) => void;
  onReset: () => void;
  onUpdateQADatabase?: (editedRows: ProcessedRow[], customerName: string) => Promise<void>;
  onAddSingleToQA?: (row: ProcessedRow, customerName: string) => Promise<void>;
  onRegenerateResponse?: (row: ProcessedRow, additionalContext: string) => Promise<RegeneratedResponse>;
  isUpdatingQA?: boolean;
}

const ResultsTable: React.FC<ResultsTableProps> = ({
  rows,
  customFields,
  projectId,
  onUpdateRow,
  onUpdateRowSources,
  onReset,
  onUpdateQADatabase,
  onAddSingleToQA,
  onRegenerateResponse,
  isUpdatingQA = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedDebug, setExpandedDebug] = useState<Set<string>>(new Set());
  const [copiedRowId, setCopiedRowId] = useState<string | null>(null);
  const [showCustomerNameModal, setShowCustomerNameModal] = useState(false);
  const [customerNameInput, setCustomerNameInput] = useState('');
  // State for individual Q&A addition
  const [singleQARow, setSingleQARow] = useState<ProcessedRow | null>(null);
  const [showSingleQAModal, setShowSingleQAModal] = useState(false);
  const [isAddingSingleQA, setIsAddingSingleQA] = useState(false);
  const [addedToQARows, setAddedToQARows] = useState<Set<string>>(new Set());
  // Shared remembered customer name (synced between bulk and individual modals)
  const [rememberedCustomerName, setRememberedCustomerName] = useState('');
  // State for regenerate modal
  const [regenerateRow, setRegenerateRow] = useState<ProcessedRow | null>(null);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [additionalContext, setAdditionalContext] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  // State for refresh sources
  const [refreshingSourcesRowId, setRefreshingSourcesRowId] = useState<string | null>(null);
  // State for add source modal
  const [addSourceRowId, setAddSourceRowId] = useState<string | null>(null);
  const [sourceSearchTerm, setSourceSearchTerm] = useState('');
  // Cached URL mappings - use ref to persist across renders without triggering re-renders
  const urlMappingsCache = useRef<{ projectId: string; mappings: Array<{ filename: string; public_url: string }> } | null>(null);
  const [availableSources, setAvailableSources] = useState<Array<{ filename: string; public_url: string }>>([]);
  const [isLoadingMappings, setIsLoadingMappings] = useState(false);

  // Generate complete response text for a row
  // Generate complete response text for a row (response + sources only, no evidence)
  const getCompleteResponseText = (row: ProcessedRow): string => {
    return formatCompleteResponse(row.response, row.sources);
  };

  // Handle copy to clipboard
  const handleCopyCompleteResponse = async (row: ProcessedRow) => {
    const text = getCompleteResponseText(row);
    const success = await copyToClipboard(text);
    if (success) {
      setCopiedRowId(row.id);
      setTimeout(() => setCopiedRowId(null), 2000);
    }
  };

  const toggleDebug = (rowId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedDebug((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  // Handle refresh sources using LLM to match documents
  const handleRefreshSources = async (row: ProcessedRow, e: React.MouseEvent) => {
    e.stopPropagation();

    if (refreshingSourcesRowId) return; // Already refreshing

    setRefreshingSourcesRowId(row.id);
    try {
      const result = await refreshSources(projectId, row.response, row.query);
      if (result.sources && result.sources.length > 0) {
        // Merge new sources with existing ones (additive - don't remove existing)
        const existingSources = row.sources || [];
        const existingDocNames = new Set(existingSources.map(s => s.doc_name));

        // Only add sources that don't already exist
        const newSources = result.sources.filter(s => !existingDocNames.has(s.doc_name));

        if (newSources.length > 0) {
          // Normalize existing sources to match expected type (null -> undefined)
          const normalizedExisting = existingSources.map(s => ({
            doc_name: s.doc_name,
            url: s.url ?? undefined
          }));
          const mergedSources = [...normalizedExisting, ...newSources];
          onUpdateRowSources(row.id, mergedSources);
        }
      }
    } catch (error) {
      console.error('Failed to refresh sources:', error);
    } finally {
      setRefreshingSourcesRowId(null);
    }
  };

  // Handle removing a source from a row
  const handleRemoveSource = (row: ProcessedRow, sourceIndex: number) => {
    const updatedSources = row.sources.filter((_, idx) => idx !== sourceIndex);
    const normalizedSources = updatedSources.map(s => ({
      doc_name: s.doc_name,
      url: s.url ?? undefined
    }));
    onUpdateRowSources(row.id, normalizedSources);
  };

  // Load URL mappings with caching
  const loadURLMappings = async () => {
    // Check cache first
    if (urlMappingsCache.current && urlMappingsCache.current.projectId === projectId) {
      setAvailableSources(urlMappingsCache.current.mappings);
      return;
    }

    setIsLoadingMappings(true);
    try {
      const result = await getURLMappings(projectId);
      if (result.mappings) {
        urlMappingsCache.current = { projectId, mappings: result.mappings };
        setAvailableSources(result.mappings);
      }
    } catch (error) {
      console.error('Failed to load URL mappings:', error);
    } finally {
      setIsLoadingMappings(false);
    }
  };

  // Open add source modal for a row
  const handleOpenAddSource = async (rowId: string) => {
    setAddSourceRowId(rowId);
    setSourceSearchTerm('');
    await loadURLMappings();
  };

  // Add a source to a row
  const handleAddSource = (row: ProcessedRow, source: { filename: string; public_url: string }) => {
    // Check if already exists
    if (row.sources.some(s => s.doc_name === source.filename)) {
      return; // Already exists
    }
    const existingSources = row.sources.map(s => ({
      doc_name: s.doc_name,
      url: s.url ?? undefined
    }));
    const newSource = { doc_name: source.filename, url: source.public_url || undefined };
    onUpdateRowSources(row.id, [...existingSources, newSource]);
  };

  // Filter available sources by search term
  const getFilteredSources = (row: ProcessedRow) => {
    const existingDocNames = new Set(row.sources.map(s => s.doc_name));
    return availableSources
      .filter(s => !existingDocNames.has(s.filename)) // Exclude already added
      .filter(s => s.filename.toLowerCase().includes(sourceSearchTerm.toLowerCase()));
  };

  const renderDebugPanel = (rowId: string, debugInfo: BatchDebugInfo) => {
    if (!expandedDebug.has(rowId)) return null;

    return (
      <div className="debug-panel">
        <div className="debug-header">
          <span className="debug-title">🔍 Debug Info</span>
          <span className={`debug-badge ${debugInfo.qa_match_used ? 'qa' : 'rag'}`}>
            {debugInfo.qa_match_used ? '📋 Q&A Match Used' : '📄 RAG Generated'}
          </span>
        </div>

        {/* Q&A Matches Section */}
        {debugInfo.qa_matches && debugInfo.qa_matches.length > 0 && (
          <div className="debug-section">
            <h4>📋 Q&A Matches (Top {debugInfo.qa_matches.length})</h4>
            <div className="debug-items">
              {debugInfo.qa_matches.map((qa, idx) => (
                <div key={idx} className={`debug-item ${idx === 0 && debugInfo.qa_match_used ? 'used' : ''}`}>
                  <div className="debug-item-header">
                    <span className={`similarity-badge ${qa.similarity_score >= 0.85 ? 'high' : 'medium'}`}>
                      {Math.round(qa.similarity_score * 100)}%
                    </span>
                    <span className="debug-meta">
                      {qa.client_name} • {qa.response_date}
                    </span>
                    {idx === 0 && debugInfo.qa_match_used && (
                      <span className="used-badge">✓ Used</span>
                    )}
                  </div>
                  <div className="debug-item-preview">{qa.matched_question}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Policy Chunks Section */}
        {debugInfo.policy_chunks && debugInfo.policy_chunks.length > 0 && (
          <div className="debug-section">
            <h4>📄 Policy Document Chunks</h4>
            <div className="debug-items">
              {debugInfo.policy_chunks.map((chunk, idx) => (
                <div key={idx} className="debug-item">
                  <div className="debug-item-header">
                    {chunk.url ? (
                      <a href={chunk.url} target="_blank" rel="noopener noreferrer">
                        📄 {chunk.doc_name}
                      </a>
                    ) : (
                      <span>📄 {chunk.doc_name}</span>
                    )}
                    <span
                      className={`similarity-badge ${chunk.similarity_score >= 0.7 ? 'high' : 'medium'}`}
                    >
                      {Math.round(chunk.similarity_score * 100)}%
                    </span>
                  </div>
                  <div className="debug-item-preview">{chunk.text_preview}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* LLM Prompt Section */}
        {debugInfo.llm_prompt && (
          <div className="debug-section">
            <h4>🤖 LLM Prompt Sent to Gemini</h4>
            <div className="debug-prompt-container">
              <button
                className="copy-prompt-btn"
                onClick={() => {
                  navigator.clipboard.writeText(debugInfo.llm_prompt || '');
                  alert('Prompt copied to clipboard!');
                }}
              >
                📋 Copy Full Prompt
              </button>
              <pre className="debug-prompt">{debugInfo.llm_prompt}</pre>
            </div>
          </div>
        )}
      </div>
    );
  };

  const filteredRows = rows.filter(row =>
    row.query.toLowerCase().includes(searchTerm.toLowerCase()) ||
    row.response.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get edited rows for the Update QA Database feature
  const editedRows = rows.filter(row => row.isEdited);

  // Get rows with no Q&A matches or all matches below 60% similarity
  // These are new question types answered by RAG that should be added to the database
  const QA_MATCH_THRESHOLD = 0.6;
  const newQuestionRows = rows.filter(row => {
    if (row.isEdited) return false; // Already counted in editedRows
    const qaMatches = row.debugInfo?.qa_matches || [];
    if (qaMatches.length === 0) return true; // No matches at all
    // Check if ALL matches are below threshold
    const hasGoodMatch = qaMatches.some(match => match.similarity_score >= QA_MATCH_THRESHOLD);
    return !hasGoodMatch;
  });

  // Combined rows to add to Q&A database
  const rowsToAddToQA = [...editedRows, ...newQuestionRows];

  const handleOpenCustomerNameModal = () => {
    if (rowsToAddToQA.length > 0) {
      // Pre-fill with remembered customer name
      setCustomerNameInput(rememberedCustomerName);
      setShowCustomerNameModal(true);
    }
  };

  const handleConfirmUpdateQA = async () => {
    if (onUpdateQADatabase && rowsToAddToQA.length > 0 && customerNameInput.trim()) {
      // Remember the customer name for future use
      setRememberedCustomerName(customerNameInput.trim());
      await onUpdateQADatabase(rowsToAddToQA, customerNameInput.trim());
      setShowCustomerNameModal(false);
    }
  };

  const handleCancelUpdateQA = () => {
    setShowCustomerNameModal(false);
  };

  // Handlers for individual Q&A addition
  const handleOpenSingleQAModal = (row: ProcessedRow, e: React.MouseEvent) => {
    e.stopPropagation();
    setSingleQARow(row);
    // Pre-fill with remembered customer name
    setCustomerNameInput(rememberedCustomerName);
    setShowSingleQAModal(true);
  };

  const handleConfirmSingleQA = async () => {
    if (onAddSingleToQA && singleQARow && customerNameInput.trim()) {
      setIsAddingSingleQA(true);
      try {
        await onAddSingleToQA(singleQARow, customerNameInput.trim());
        // Remember the customer name for future use
        setRememberedCustomerName(customerNameInput.trim());
        // Mark this row as added to Q&A
        setAddedToQARows(prev => new Set(prev).add(singleQARow.id));
        setShowSingleQAModal(false);
        setSingleQARow(null);
      } finally {
        setIsAddingSingleQA(false);
      }
    }
  };

  const handleCancelSingleQA = () => {
    setShowSingleQAModal(false);
    setSingleQARow(null);
  };

  // Handlers for regenerate response
  const handleOpenRegenerateModal = (row: ProcessedRow, e: React.MouseEvent) => {
    e.stopPropagation();
    setRegenerateRow(row);
    setAdditionalContext('');
    setShowRegenerateModal(true);
  };

  const handleConfirmRegenerate = async () => {
    if (onRegenerateResponse && regenerateRow && additionalContext.trim()) {
      setIsRegenerating(true);
      try {
        const result = await onRegenerateResponse(regenerateRow, additionalContext.trim());
        // Update the row with the new response
        onUpdateRow(regenerateRow.id, 'response', result.response);
        setShowRegenerateModal(false);
        setRegenerateRow(null);
        setAdditionalContext('');
      } finally {
        setIsRegenerating(false);
      }
    }
  };

  const handleCancelRegenerate = () => {
    setShowRegenerateModal(false);
    setRegenerateRow(null);
    setAdditionalContext('');
  };

  const handleExportCSV = () => {
    const headers = ['Query', 'Response', 'Sources', 'Complete Response', 'Confidence', ...customFields.map(f => f.name)];
    const csvRows = [
      headers.join(','),
      ...rows.map(row => {
        // Format sources as "DocName (URL); DocName2 (URL2)" - includes URLs that were previously in Evidence
        const sourcesStr = row.sources
          .map(s => s.url ? `${s.doc_name} (${s.url})` : s.doc_name)
          .join('; ');

        // Generate complete response text
        const completeResponseText = getCompleteResponseText(row);

        const values = [
          escapeCSV(row.query),
          escapeCSV(row.response),
          escapeCSV(sourcesStr),
          escapeCSV(completeResponseText),
          row.confidence.toString(),
          ...customFields.map(f => escapeCSV(row.customFields[f.name] || '')),
        ];
        return values.join(',');
      }),
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `compliance_responses_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const escapeCSV = (value: string): string => {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const getConfidenceColor = (confidence: number): string => {
    if (confidence >= 0.8) return '#4caf50';
    if (confidence >= 0.5) return '#ff9800';
    return '#f44336';
  };

  return (
    <div className="results-table">
      <div className="results-header">
        <h2>✅ Processed Results ({rows.length} questions)</h2>
        <div className="header-actions">
          <input
            type="text"
            placeholder="🔍 Search questions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {rowsToAddToQA.length > 0 && onUpdateQADatabase && (
            <button
              onClick={handleOpenCustomerNameModal}
              className="update-qa-button"
              disabled={isUpdatingQA}
            >
              {isUpdatingQA ? '⏳ Updating...' : `💾 Update QA Database (${rowsToAddToQA.length})`}
            </button>
          )}
          <button onClick={handleExportCSV} className="export-button">
            📥 Download CSV
          </button>
          <button onClick={onReset} className="reset-button">
            🔄 New Upload
          </button>
        </div>
      </div>

      {/* Customer Name Modal */}
      {showCustomerNameModal && (
        <div className="modal-overlay">
          <div className="modal-content customer-name-modal">
            <h3>Update Q&A Database</h3>
            <p>Enter the customer/client name for these Q&A entries:</p>
            <input
              type="text"
              value={customerNameInput}
              onChange={(e) => setCustomerNameInput(e.target.value)}
              placeholder="e.g., ABC Corporation"
              className="customer-name-input"
              autoFocus={!rememberedCustomerName}
            />
            <p className="modal-hint">
              {rememberedCustomerName ? '✓ Using remembered customer name' : 'This is the company that sent you this questionnaire.'}
            </p>
            <div className="qa-summary">
              {editedRows.length > 0 && (
                <div className="qa-summary-item edited">
                  <span className="qa-count">{editedRows.length}</span>
                  <span className="qa-label">Edited responses</span>
                </div>
              )}
              {newQuestionRows.length > 0 && (
                <div className="qa-summary-item new">
                  <span className="qa-count">{newQuestionRows.length}</span>
                  <span className="qa-label">New question types (no existing Q&A match ≥60%)</span>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button onClick={handleCancelUpdateQA} className="modal-cancel-btn">
                Cancel
              </button>
              <button
                onClick={handleConfirmUpdateQA}
                className="modal-confirm-btn"
                disabled={!customerNameInput.trim() || isUpdatingQA}
              >
                {isUpdatingQA ? 'Updating...' : `Add ${rowsToAddToQA.length} Q&A Pairs`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single Q&A Addition Modal */}
      {showSingleQAModal && singleQARow && (
        <div className="modal-overlay">
          <div className="modal-content customer-name-modal single-qa-modal">
            <h3>➕ Add to Q&A Database</h3>
            <p>Add this response to your Q&A knowledge base:</p>
            <div className="single-qa-preview">
              <div className="preview-question">
                <strong>Q:</strong> {singleQARow.query.length > 100 ? singleQARow.query.substring(0, 100) + '...' : singleQARow.query}
              </div>
              <div className="preview-answer">
                <strong>A:</strong> {singleQARow.response.length > 150 ? singleQARow.response.substring(0, 150) + '...' : singleQARow.response}
              </div>
            </div>
            <input
              type="text"
              value={customerNameInput}
              onChange={(e) => setCustomerNameInput(e.target.value)}
              placeholder="Customer name (e.g., ABC Corporation)"
              className="customer-name-input"
              autoFocus={!rememberedCustomerName}
            />
            <p className="modal-hint">
              {rememberedCustomerName ? '✓ Using remembered customer name' : 'The company that sent this questionnaire.'}
            </p>
            <div className="modal-actions">
              <button onClick={handleCancelSingleQA} className="modal-cancel-btn">
                Cancel
              </button>
              <button
                onClick={handleConfirmSingleQA}
                className="modal-confirm-btn"
                disabled={!customerNameInput.trim() || isAddingSingleQA}
              >
                {isAddingSingleQA ? '⏳ Adding...' : '✅ Add to Q&A'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Response Modal */}
      {showRegenerateModal && regenerateRow && (
        <div className="modal-overlay">
          <div className="modal-content regenerate-modal">
            <h3>🔄 Regenerate Response</h3>
            <p>Provide additional context to improve the response:</p>
            <div className="single-qa-preview">
              <div className="preview-question">
                <strong>Question:</strong> {regenerateRow.query}
              </div>
            </div>
            <textarea
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              placeholder="Enter additional context, clarifications, or specific requirements..."
              className="regenerate-context-input"
              rows={4}
              autoFocus
            />
            <p className="modal-hint">
              This context will be combined with the question to generate a better response.
            </p>
            <div className="modal-actions">
              <button onClick={handleCancelRegenerate} className="modal-cancel-btn">
                Cancel
              </button>
              <button
                onClick={handleConfirmRegenerate}
                className="modal-confirm-btn regenerate-confirm-btn"
                disabled={!additionalContext.trim() || isRegenerating}
              >
                {isRegenerating ? '⏳ Regenerating...' : '🔄 Regenerate'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="table-container">
        {filteredRows.map((row) => {
          // Check if this is a "new question type" (no good Q&A match)
          const isNewQuestionType = newQuestionRows.some(r => r.id === row.id);

          return (
          <div
            key={row.id}
            className={`result-row ${row.isEdited ? 'edited' : ''} ${isNewQuestionType ? 'new-question' : ''}`}
          >
            {/* Always visible: Question, Response, Sources - all editable */}
            <div className="row-main-content">
              <div className="row-header-inline">
                <div className="row-meta-inline">
                  <span className="row-number">#{rows.indexOf(row) + 1}</span>
                  <span
                    className="confidence-badge"
                    style={{ background: getConfidenceColor(row.confidence) }}
                  >
                    {Math.round(row.confidence * 100)}%
                  </span>
                  {row.isEdited && <span className="edited-badge">Edited</span>}
                  {isNewQuestionType && <span className="new-question-badge">New Q</span>}
                </div>
                <div className="row-actions-inline">
                  <button
                    className={`copy-complete-btn ${copiedRowId === row.id ? 'copied' : ''}`}
                    onClick={() => handleCopyCompleteResponse(row)}
                    title="Copy complete response"
                  >
                    {copiedRowId === row.id ? '✓ Copied!' : '📋 Copy'}
                  </button>
                </div>
              </div>

              {/* Question - read only */}
              <div className="field-group">
                <label>Question</label>
                <div className="field-value readonly">{row.query}</div>
              </div>

              {/* Response - editable with regenerate button */}
              <div className="field-group">
                <div className="response-header">
                  <label>Response</label>
                  {onRegenerateResponse && (
                    <button
                      className="regenerate-inline-btn"
                      onClick={(e) => handleOpenRegenerateModal(row, e)}
                      title="Regenerate response with additional context"
                    >
                      🔄 Regenerate
                    </button>
                  )}
                </div>
                <textarea
                  value={row.response}
                  onChange={(e) => onUpdateRow(row.id, 'response', e.target.value)}
                  rows={4}
                  className={`response-textarea ${row.isEdited ? 'field-edited' : ''}`}
                />
              </div>

              {/* Sources - displayed with links, remove buttons, and add source */}
              <div className="field-group">
                <div className="sources-header">
                  <label>Sources</label>
                  <button
                    className={`refresh-sources-btn ${refreshingSourcesRowId === row.id ? 'refreshing' : ''}`}
                    onClick={(e) => handleRefreshSources(row, e)}
                    disabled={refreshingSourcesRowId !== null}
                    title="Re-analyze response to find all referenced documents"
                  >
                    {refreshingSourcesRowId === row.id ? '⏳ Refreshing...' : '🔄 Refresh'}
                  </button>
                </div>
                {row.sources && row.sources.length > 0 ? (
                  <ul className="sources-list">
                    {row.sources.map((source, idx) => (
                      <li key={idx} className="source-item">
                        <button
                          className="remove-source-btn"
                          onClick={() => handleRemoveSource(row, idx)}
                          title="Remove this source"
                        >
                          ✕
                        </button>
                        {source.url ? (
                          <a href={source.url} target="_blank" rel="noopener noreferrer">
                            {source.doc_name}
                          </a>
                        ) : (
                          <span>{source.doc_name}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="no-sources">No sources available. Click Refresh to find matching documents.</p>
                )}

                {/* Add Source Button - below sources list */}
                {addSourceRowId !== row.id && (
                  <button
                    className="add-source-inline-btn"
                    onClick={() => handleOpenAddSource(row.id)}
                    title="Add a source document"
                  >
                    <span className="add-icon">+</span>
                    <span className="add-text">Add Source</span>
                  </button>
                )}

                {/* Add Source Search Panel */}
                {addSourceRowId === row.id && (
                  <div className="add-source-panel">
                    <div className="add-source-header">
                      <span>Search & Add Sources</span>
                      <button
                        className="close-add-source"
                        onClick={() => setAddSourceRowId(null)}
                      >
                        ✕
                      </button>
                    </div>
                    <input
                      type="text"
                      className="source-search-input"
                      placeholder="Search documents..."
                      value={sourceSearchTerm}
                      onChange={(e) => setSourceSearchTerm(e.target.value)}
                      autoFocus
                    />
                    {isLoadingMappings ? (
                      <div className="source-search-loading">Loading documents...</div>
                    ) : (
                      <ul className="source-search-results">
                        {getFilteredSources(row).slice(0, 10).map((source, idx) => (
                          <li
                            key={idx}
                            className="source-search-item"
                            onClick={() => handleAddSource(row, source)}
                          >
                            <span className="source-name">{source.filename}</span>
                            {source.public_url && (
                              <span className="source-url-preview">
                                {source.public_url.substring(0, 40)}...
                              </span>
                            )}
                          </li>
                        ))}
                        {getFilteredSources(row).length === 0 && sourceSearchTerm && (
                          <li className="source-search-no-results">No matching documents found</li>
                        )}
                        {getFilteredSources(row).length === 0 && !sourceSearchTerm && availableSources.length > 0 && (
                          <li className="source-search-hint">Type to search {availableSources.length} available documents</li>
                        )}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {/* Custom Fields - editable */}
              {customFields.map((field) => (
                <div key={field.name} className="field-group">
                  <label>{field.name}</label>
                  <input
                    type="text"
                    value={row.customFields[field.name] || ''}
                    onChange={(e) => onUpdateRow(row.id, `customFields.${field.name}`, e.target.value)}
                    className={row.isEdited ? 'field-edited' : ''}
                  />
                </div>
              ))}

              {/* Action buttons section */}
              <div className="row-action-buttons">
                {/* Add to Q&A button - only show if handler provided and not already added */}
                {onAddSingleToQA && !addedToQARows.has(row.id) && (
                  <button
                    className="add-single-qa-button"
                    onClick={(e) => handleOpenSingleQAModal(row, e)}
                    title="Add this Q&A pair to knowledge base"
                  >
                    ➕ Add to Q&A
                  </button>
                )}
                {addedToQARows.has(row.id) && (
                  <span className="added-to-qa-badge">✓ Added to Q&A</span>
                )}

                {/* Debug button */}
                {row.debugInfo && (
                  <button
                    className={`debug-toggle-button ${expandedDebug.has(row.id) ? 'expanded' : ''}`}
                    onClick={(e) => toggleDebug(row.id, e)}
                  >
                    🔍 {expandedDebug.has(row.id) ? 'Hide' : 'Show'} Debug Info
                  </button>
                )}
              </div>

              {/* Debug panel - collapsible */}
              {row.debugInfo && renderDebugPanel(row.id, row.debugInfo)}
            </div>
          </div>
        );
        })}
      </div>

      {filteredRows.length === 0 && (
        <div className="no-results">
          {searchTerm ? 'No matching questions found' : 'No results to display'}
        </div>
      )}
    </div>
  );
};

export default ResultsTable;

